export const ReservedRoutes = {
  Product: 'product',
  Category: 'category',
  Search: 'search',
  MyStadium: 'my-stadium',
  Checkout: 'checkout',
  Club: 'club',
  Brand: 'brand',
  Faq: 'customer-service/faq',
  CustomerService: 'customer-service',
  Store: 'stores',
  Root: '',
  Sandbox: 'sandbox',
  Sitemap: 'sitemap',
  StoryPreview: 'story-preview',
  OnlineReturns: 'online-returns',
  WithdrawalReturns: 'angerratt-retur',
  WithdrawalReturnsFinland: 'palautusoikeus',
  BeforeDeliveryReclaim: 'angerratt-innan-leverans',
  BeforeDeliveryReclaimFinland: 'peruuta-ennen-toimitusta',
  TrackShipment: 'track-shipment',
} as const;

const InternalRoutes = {
  Product: '/p',
  Category: '/c',
  Search: '/s',
  Default: '/a',
  MyStadium: '/my-stadium',
  Checkout: '/checkout',
  Club: '/club',
  Brand: '/brand',
  Faq: '/faq',
  CustomerService: '/customer-service',
  Store: '/stores',
  Root: '',
  Sandbox: '/sandbox',
  Sitemap: '/sitemap',
  StoryPreview: '/story-preview',
  OnlineReturns: '/online-returns',
  WithdrawalReturns: '/withdrawal-returns',
  WithdrawalReturnsFinland: '/withdrawal-returns',
  BeforeDeliveryReclaim: '/before-delivery-reclaim',
  BeforeDeliveryReclaimFinland: '/before-delivery-reclaim',
  TrackShipment: '/track-shipment',
};

export type InternalRoutes =
  (typeof InternalRoutes)[keyof typeof InternalRoutes];

const FALLBACK_CHANNEL_ROUTE = 'stadium/se';

const localizedRoutes = {
  se: {
    TrackShipment: 'spara-leverans',
  },
  fi: {
    TrackShipment: 'seuraa-toimitusta',
  },
  no: {
    TrackShipment: 'spore-levering',
  },
} as const;

export const getTrackShipmentRoute = (market: 'se' | 'fi' | 'no') =>
  localizedRoutes[market].TrackShipment;

// Sorted longest-prefix first so deeper routes (e.g. 'customer-service/faq') are
// matched before their shorter prefixes.
const ROUTE_LOOKUP = (
  Object.keys(ReservedRoutes) as (keyof typeof ReservedRoutes)[]
)
  .sort((a, b) => {
    return (
      ReservedRoutes[b].split('/').length - ReservedRoutes[a].split('/').length
    );
  })
  .map((key) => [ReservedRoutes[key], InternalRoutes[key]] as [string, string]);

const localizedRouteMarkets = new Map<string, string>([
  [ReservedRoutes.WithdrawalReturns, 'se'],
  [ReservedRoutes.WithdrawalReturnsFinland, 'fi'],
  [ReservedRoutes.BeforeDeliveryReclaim, 'se'],
  [ReservedRoutes.BeforeDeliveryReclaimFinland, 'fi'],
  [localizedRoutes.se.TrackShipment, 'se'],
  [localizedRoutes.fi.TrackShipment, 'fi'],
  [localizedRoutes.no.TrackShipment, 'no'],
]);

const localizedRouteLookup = Object.values(localizedRoutes).map(
  (routes) =>
    [routes.TrackShipment, InternalRoutes.TrackShipment] as [string, string],
);

const isRouteAvailableForChannel = (route: string, channelRoute: string) => {
  const requiredMarket = localizedRouteMarkets.get(route);

  if (!requiredMarket) {
    return true;
  }

  const [concept, market] = channelRoute.split('/');
  const supportsLocalizedRoutes =
    concept === 'stadium' || concept === 'stadiumoutlet';

  return supportsLocalizedRoutes && market === requiredMarket;
};

const allRoutes = [...ROUTE_LOOKUP, ...localizedRouteLookup];
export const parseDomain = (domain: string | null) => {
  if (!domain) {
    return FALLBACK_CHANNEL_ROUTE;
  }

  if (domain.includes('localhost')) {
    return FALLBACK_CHANNEL_ROUTE;
  }

  if (domain.endsWith('vercel.app')) {
    return FALLBACK_CHANNEL_ROUTE;
  }

  const portNumberRegex = /:\d+/g;
  const domainParts = domain.replace(portNumberRegex, '').split('.');

  return domainParts.slice(domainParts.length - 2).join('/');
};

const mapToInternalRoute = async (
  incomingPath: string,
  channelRoute: string,
  draftMode: boolean,
  flagCode: string,
  contentReleaseVersion: string | undefined,
) => {
  const pathContent = incomingPath.startsWith('/')
    ? incomingPath.slice(1)
    : incomingPath;

  const match = allRoutes.find(
    ([k]) =>
      (pathContent === k || pathContent.startsWith(`${k}/`)) &&
      isRouteAvailableForChannel(k, channelRoute),
  );
  const route = match?.[0] ?? '';
  const internalPath = match?.[1] ?? InternalRoutes.Default;

  const contentReleasePart = contentReleaseVersion
    ? `-${contentReleaseVersion}`
    : '';
  const draftPart = draftMode ? `1${contentReleasePart}` : '0';

  if (internalPath === InternalRoutes.Default) {
    return `/${flagCode}/${draftPart}/${channelRoute}${internalPath}${incomingPath}`;
  }

  return `/${flagCode}/${draftPart}/${channelRoute}${internalPath}${incomingPath.slice(1 + route.length)}`;
};

export const getInternalRoute = ({
  domain,
  incomingPath,
  draftMode,
  flagCode,
  contentReleaseVersion,
}: {
  domain: string | null;
  incomingPath: string;
  draftMode?: boolean;
  flagCode: string;
  contentReleaseVersion: string | undefined;
}) => {
  const channelRoute = parseDomain(domain);
  const internalRoute = mapToInternalRoute(
    incomingPath,
    channelRoute,
    Boolean(draftMode),
    flagCode,
    contentReleaseVersion,
  );
  return internalRoute;
};
